import React, { useState, useEffect, useCallback, useRef } from 'react';
import execucaoTecnicaService from '../../services/execucaoTecnica.service';

// ════════════════════════════════════════════════════════════════════════════
// ExecucaoTecnicaModal.jsx — r240
// ────────────────────────────────────────────────────────────────────────────
// Modal do técnico que está tocando o projeto. Layout do modelo Exectec.png:
//
//   ┌ 6 indicadores ────────────────────────────────────────────────────────┐
//   │ Data Considerada │ Avanço Esperado │ Avanço Realizado │ Horas ...     │
//   └───────────────────────────────────────────────────────────────────────┘
//   ▸ 10.1 - Descrição do item          (barra azul cheia, expansível)
//     └ 10.1.1 - Atividade              (card branco, borda azul)
//     └ 10.1.2 - Atividade
//
// A linha do TÓPICO mostra PREVISTO x REALIZADO em duas alturas:
//   📅 data início prevista   ⏱ horas previstas
//   ☑ data fim realizada +0D  ⏰ horas executadas +0H
// A atividade mostra só as datas (r240): as horas somam no tópico, que é onde
// a comparação é lida.
//
// O botão à direita de cada atividade é o gancho pras "outras
// funcionalidades" (apontar avanço, iniciar/concluir) que ainda serão feitas —
// por isso ele chama `onAcaoAtividade` e a tela decide o que fazer.
// ════════════════════════════════════════════════════════════════════════════

const COR = {
  azul:     '#003399', azulBg:  '#eff6ff', azulClaro: '#1e6fd9',
  borda:    '#e8eaed', texto:   '#404040', muted: '#888',
  branco:   '#fff',    cinzaBg: '#fafafa',
  vermelho: '#e53935', vermBg:  '#fff0f0',
  verde:    '#1D9E75',
};

const FONTE = "'Manrope', sans-serif";

// ─── Helpers ────────────────────────────────────────────────────────────────
const dataBR = (iso) => {
  if (!iso) return '—';
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(iso);
};

// Horas em HH:MM — o modelo mostra 40:00, não 40,00
const hhmm = (horas) => {
  const v = Number(horas) || 0;
  const neg = v < 0;
  const abs = Math.abs(v);
  const h = Math.floor(abs);
  const m = Math.round((abs - h) * 60);
  return `${neg ? '-' : ''}${h}:${String(m).padStart(2, '0')}`;
};

// Badge de desvio: "+0D" / "+2D" / "-1D". Verde quando adiantado ou no prazo,
// vermelho quando estourou.
const badgeDesvio = (valor, sufixo) => {
  const v = Number(valor) || 0;
  const atrasado = v > 0;
  return {
    texto: `${v > 0 ? '+' : ''}${sufixo === 'H' ? hhmm(v) : v}${sufixo}`,
    cor: atrasado ? COR.vermelho : COR.azulClaro,
  };
};

// ─── Filtro das horas consideradas (r238) ───────────────────────────────────
// Admin escolhe de quem são as horas lançadas que entram no indicador "Horas
// Lançadas" — a lista traz só quem de fato apontou hora neste projeto, com o
// total de cada um ao lado. Quem não é admin não escolhe: vê o próprio nome
// fixo, porque o backend já recorta pelas horas dele.
//
// r239: virou MULTI-SELEÇÃO. Não é um <select multiple> — aquele obriga
// ctrl+clique e some com a seleção anterior num clique distraído. É o mesmo
// desenho dos painéis de filtro do portal: um botão que abre uma lista de
// checkboxes, com "Todos" no topo. Nenhum marcado = todos.
function FiltroHoras({ filtro, selecionados, onTrocar }) {
  const [aberto, setAberto] = useState(false);
  const caixaRef = useRef(null);

  // Fecha ao clicar fora. O modal já engole o ESC (fecha a tela inteira),
  // então aqui só o clique fora mesmo.
  useEffect(() => {
    if (!aberto) return undefined;
    const onClique = (e) => {
      if (caixaRef.current && !caixaRef.current.contains(e.target)) setAberto(false);
    };
    document.addEventListener('mousedown', onClique);
    return () => document.removeEventListener('mousedown', onClique);
  }, [aberto]);

  if (!filtro) return null;
  const opcoes = filtro.colaboradores || [];
  const marcados = new Set(selecionados || []);

  const caixa = {
    display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
    marginBottom: '12px', padding: '8px 12px',
    backgroundColor: COR.branco, border: `1px solid ${COR.borda}`, borderRadius: '8px',
  };
  // r240: sem `text-transform: uppercase` — o rótulo é "Analistas", só a
  // inicial maiúscula, e não o "ANALISTAS" dos painéis de filtro do browse.
  const rotulo = {
    fontSize: '11px', fontWeight: '700', color: COR.azul,
    letterSpacing: '0.5px',
  };

  if (!filtro.admin) {
    const meu = opcoes[0];
    return (
      <div style={caixa}>
        <span style={rotulo}>Analistas</span>
        <Pilula texto={meu ? `${meu.nome} · ${hhmm(meu.horas)}` : 'Somente as suas'} />
        <span style={{ fontSize: '12px', color: COR.muted }}>
          somente as horas apontadas por você
        </span>
      </div>
    );
  }

  const alternar = (id) => {
    const n = new Set(marcados);
    n.has(id) ? n.delete(id) : n.add(id);
    onTrocar([...n]);
  };

  // Resumo no botão: nenhum marcado = todos; um só = o nome; vários = contagem.
  const resumo = marcados.size === 0
    ? 'Todos os analistas'
    : marcados.size === 1
      ? (opcoes.find(c => marcados.has(c.id))?.nome || '1 selecionado')
      : `${marcados.size} selecionados`;

  return (
    <div style={caixa}>
      <span style={rotulo}>Analistas</span>

      <div ref={caixaRef} style={{ position: 'relative' }}>
        <button
          type="button"
          onClick={() => setAberto(a => !a)}
          style={{
            height: '32px', minWidth: '260px', padding: '0 10px',
            border: `1px solid ${COR.borda}`, borderRadius: '5px',
            backgroundColor: COR.branco, color: COR.texto,
            fontSize: '13px', fontFamily: FONTE, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px',
          }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {resumo}
          </span>
          <span style={{ color: COR.azul, fontSize: '10px' }}>{aberto ? '▲' : '▼'}</span>
        </button>

        {aberto && (
          <div style={{
            position: 'absolute', top: '36px', left: 0, zIndex: 5,
            minWidth: '300px', maxHeight: '260px', overflowY: 'auto',
            backgroundColor: COR.branco, border: `1px solid ${COR.borda}`,
            borderRadius: '6px', boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
            padding: '6px',
          }}>
            {opcoes.length === 0 ? (
              <div style={{ padding: '10px', fontSize: '12px', color: COR.muted }}>
                Ninguém apontou horas neste projeto ainda.
              </div>
            ) : (
              <>
                {/* "Todos" = limpar a seleção; é o mesmo estado de nada marcado */}
                <label style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '6px 8px', borderRadius: '4px', cursor: 'pointer',
                  fontSize: '13px', fontWeight: '700', color: COR.azul,
                  borderBottom: `1px solid ${COR.borda}`,
                }}>
                  <input type="checkbox" checked={marcados.size === 0}
                    onChange={() => onTrocar([])} style={{ cursor: 'pointer' }} />
                  Todos os analistas
                </label>
                {opcoes.map(c => (
                  <label key={c.id} style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '6px 8px', borderRadius: '4px', cursor: 'pointer',
                    fontSize: '13px', color: COR.texto,
                  }}>
                    <input type="checkbox" checked={marcados.has(c.id)}
                      onChange={() => alternar(c.id)} style={{ cursor: 'pointer' }} />
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis',
                                   whiteSpace: 'nowrap' }}>{c.nome}</span>
                    <span style={{ color: COR.azulClaro, fontWeight: '700' }}>{hhmm(c.horas)}</span>
                  </label>
                ))}
              </>
            )}
          </div>
        )}
      </div>

      <span style={{ fontSize: '12px', color: COR.muted }}>
        {opcoes.length === 0
          ? 'ninguém apontou horas neste projeto ainda'
          : 'quem apontou horas neste projeto'}
      </span>
    </div>
  );
}

// ─── Indicador do topo ──────────────────────────────────────────────────────
function Indicador({ label, valor, alerta }) {
  const cor = alerta ? COR.vermelho : COR.azul;
  return (
    <div style={{
      flex: '1 1 150px', minWidth: '150px',
      border: `2px solid ${cor}`, borderRadius: '8px',
      backgroundColor: alerta ? COR.vermBg : COR.branco,
      padding: '8px 12px',
    }}>
      <div style={{ fontSize: '12px', fontWeight: '600', color: cor, lineHeight: 1.2 }}>
        {label}
      </div>
      <div style={{
        fontSize: '20px', fontWeight: '700', color: cor,
        textAlign: 'right', lineHeight: 1.2, marginTop: '2px',
      }}>{valor}</div>
    </div>
  );
}

// ─── Pílula (etapa, responsável) ────────────────────────────────────────────
function Pilula({ texto, invertido }) {
  if (!texto) return null;
  return (
    <span style={{
      display: 'inline-block', padding: '1px 10px', borderRadius: '20px',
      fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap',
      backgroundColor: invertido ? 'rgba(255,255,255,0.18)' : COR.azulClaro,
      color: COR.branco,
      border: invertido ? '1px solid rgba(255,255,255,0.45)' : 'none',
    }}>{texto}</span>
  );
}

// ─── Barra de progresso ─────────────────────────────────────────────────────
// O % fica DENTRO da barra quando há preenchimento suficiente e fora quando a
// barra está curta, senão o número some sobre o azul.
function Barra({ percentual, invertido }) {
  const p = Math.max(0, Math.min(100, Number(percentual) || 0));
  const dentro = p >= 25;
  return (
    <div style={{
      position: 'relative', width: '100%', height: '26px',
      border: `2px solid ${invertido ? COR.branco : COR.azulClaro}`,
      borderRadius: '6px', overflow: 'hidden',
      backgroundColor: invertido ? 'rgba(255,255,255,0.15)' : COR.branco,
    }}>
      <div style={{
        width: `${p}%`, height: '100%',
        backgroundColor: invertido ? COR.branco : COR.azulClaro,
        transition: 'width 0.2s',
      }} />
      <span style={{
        position: 'absolute', top: 0, bottom: 0,
        left: dentro ? '10px' : `calc(${p}% + 10px)`,
        display: 'flex', alignItems: 'center',
        fontSize: '12px', fontWeight: '700',
        color: dentro
          ? (invertido ? COR.azul : COR.branco)
          : (invertido ? COR.branco : COR.azul),
      }}>{p}%</span>
    </div>
  );
}

// ─── Ícones ─────────────────────────────────────────────────────────────────
const Svg = ({ children, tam = 14, cor = 'currentColor' }) => (
  <svg width={tam} height={tam} viewBox="0 0 24 24" fill="none" stroke={cor}
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
       style={{ flexShrink: 0 }}>{children}</svg>
);
const IconeCalendario = (p) => (
  <Svg {...p}><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></Svg>
);
const IconeCalendarioCheck = (p) => (
  <Svg {...p}>
    <rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" />
    <path d="M8.5 15l2.2 2.2 4.3-4.6" />
  </Svg>
);
const IconeCronometro = (p) => (
  <Svg {...p}><circle cx="12" cy="13" r="8" /><path d="M12 9v4l2.5 1.5" /><path d="M9 2h6" /></Svg>
);
const IconeDespertador = (p) => (
  <Svg {...p}>
    <circle cx="12" cy="13" r="7.5" /><path d="M12 9.5V13l2.4 1.4" />
    <path d="M5 4.5L2.6 6.6M19 4.5l2.4 2.1" />
  </Svg>
);
// r239: reticências — o botão de "ações" da atividade. Era um capacete de
// obra; três pontos dizem "tem mais coisa aqui" sem prometer uma ação
// específica, que é justamente o caso enquanto o menu não existe.
// Bolinhas cheias, então não passa pelo <Svg> (que é fill:none + stroke).
const IconeReticencias = ({ tam = 18, cor = 'currentColor' }) => (
  <svg width={tam} height={tam} viewBox="0 0 24 24" fill={cor} style={{ flexShrink: 0 }}>
    <circle cx="5" cy="12" r="2" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="19" cy="12" r="2" />
  </svg>
);
const IconeMais = ({ aberto }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={COR.branco}
       strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0 }}>
    <circle cx="12" cy="12" r="10" />
    <path d="M7 12h10" />
    {!aberto && <path d="M12 7v10" />}
  </svg>
);

// ─── Bloco de datas / horas (previsto em cima, realizado embaixo) ───────────
function ParDatas({ prev, real, desvioDias: dd, invertido }) {
  const b = badgeDesvio(dd, 'D');
  const corTexto = invertido ? COR.branco : COR.azulClaro;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '120px' }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: '6px',
                     fontSize: '13px', fontWeight: '700', color: corTexto }}>
        <IconeCalendario cor={corTexto} /> {dataBR(prev)}
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: '6px',
                     fontSize: '13px', fontWeight: '700', color: corTexto }}>
        <IconeCalendarioCheck cor={corTexto} /> {dataBR(real)}
        <Badge texto={b.texto} cor={invertido ? COR.branco : b.cor} invertido={invertido} />
      </span>
    </div>
  );
}

function ParHoras({ prev, real, desvioHoras: dh, invertido }) {
  const b = badgeDesvio(dh, 'H');
  const corTexto = invertido ? COR.branco : COR.azulClaro;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '110px' }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: '6px',
                     fontSize: '13px', fontWeight: '700', color: corTexto }}>
        <IconeCronometro cor={corTexto} /> {hhmm(prev)}
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: '6px',
                     fontSize: '13px', fontWeight: '700', color: corTexto }}>
        <IconeDespertador cor={corTexto} /> {hhmm(real)}
        <Badge texto={b.texto} cor={invertido ? COR.branco : b.cor} invertido={invertido} />
      </span>
    </div>
  );
}

function Badge({ texto, cor, invertido }) {
  return (
    <span style={{
      padding: '1px 8px', borderRadius: '20px',
      fontSize: '11px', fontWeight: '700',
      backgroundColor: invertido ? 'rgba(255,255,255,0.2)' : cor,
      color: COR.branco,
      border: invertido ? '1px solid rgba(255,255,255,0.5)' : 'none',
      whiteSpace: 'nowrap',
    }}>{texto}</span>
  );
}

// ════════════════════════════════════════════════════════════════════════════
function ExecucaoTecnicaModal({ aberto, projetoId, onFechar, onAcaoAtividade }) {
  const [dados, setDados]           = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro]             = useState('');
  // Tópicos recolhidos. Abre TUDO FECHADO (r237): a lista de tópicos inteira
  // aberta enterra o topo do modal; o técnico expande o que vai tocar.
  const [recolhidos, setRecolhidos] = useState(new Set());

  // r238/r239: de quem são as horas consideradas. Lista vazia = todas (só
  // admin chega nisso; pro resto o backend força o próprio colaborador e
  // ignora o que vier).
  const [colabHoras, setColabHoras] = useState([]);

  // Marcar/desmarcar gente no filtro dispara uma carga por clique. Sem um
  // guarda, uma resposta lenta de um clique antigo chegaria depois e pintaria
  // a tela com o recorte errado — só a carga mais recente pode escrever.
  const cargaAtual = useRef(0);

  // `recolher` só é true na carga de abertura: trocar o filtro de horas não
  // pode fechar de volta os tópicos que o técnico acabou de abrir.
  const carregar = useCallback(async (colabs = [], recolher = true) => {
    if (!projetoId) return;
    const minhaCarga = ++cargaAtual.current;
    setCarregando(true); setErro('');
    try {
      const d = await execucaoTecnicaService.obterArvore(projetoId, colabs);
      if (minhaCarga !== cargaAtual.current) return;
      setDados(d);
      // Recolhe todos os tópicos que vieram (só dá pra saber quais são
      // depois da carga — por isso aqui e não no efeito de abertura).
      if (recolher) setRecolhidos(new Set((d?.topicos || []).map(t => t.topico)));
    } catch (err) {
      if (minhaCarga !== cargaAtual.current) return;
      setErro(err.response?.data?.mensagem || 'Erro ao carregar a execução do projeto.');
      setDados(null);
    } finally {
      if (minhaCarga === cargaAtual.current) setCarregando(false);
    }
  }, [projetoId]);

  // O estado de recolhido é definido dentro de `carregar` (tudo fechado).
  useEffect(() => { if (aberto) { setColabHoras([]); carregar([], true); } }, [aberto, carregar]);

  const trocarColaboradores = (lista) => { setColabHoras(lista); carregar(lista, false); };

  // Fecha com ESC + trava o scroll do fundo enquanto o modal está aberto
  useEffect(() => {
    if (!aberto) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onFechar(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [aberto, onFechar]);

  if (!aberto) return null;

  const alternar = (topico) => setRecolhidos(s => {
    const n = new Set(s); n.has(topico) ? n.delete(topico) : n.add(topico); return n;
  });

  const ind = dados?.indicadores;
  // Avanço realizado abaixo do esperado = destaque vermelho (como no modelo)
  const atrasado = !!ind && ind.avanco_realizado < ind.avanco_esperado;

  return (
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget) onFechar(); }}
      style={{
        // fixed, não absolute — mesma lição da r232: com absolute o modal
        // ancorava no topo da página e sumia da vista em lista rolada.
        position: 'fixed', inset: 0, zIndex: 10200,
        backgroundColor: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '24px 16px', overflowY: 'auto', fontFamily: FONTE,
      }}
    >
      <div style={{
        width: '100%', maxWidth: '1280px',
        maxHeight: 'calc(100vh - 48px)',
        backgroundColor: COR.branco, borderRadius: '10px',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
      }}>
        {/* ═══ Cabeçalho ═══ */}
        <div style={{
          backgroundColor: COR.azul, color: COR.branco,
          padding: '14px 20px', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '14px',
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '16px', fontWeight: '700', whiteSpace: 'nowrap',
                          overflow: 'hidden', textOverflow: 'ellipsis' }}>
              Execução Técnica — {dados?.projeto?.id || projetoId}
              {dados?.projeto?.descricao ? ` · ${dados.projeto.descricao}` : ''}
            </div>
            <div style={{ fontSize: '12px', opacity: 0.85, marginTop: '2px' }}>
              {dados?.projeto?.cliente_fantasia || dados?.projeto?.cliente_razao_social || ''}
              {dados?.versao ? ` · Quantificação ${dados.versao}` : ''}
            </div>
          </div>
          <button onClick={onFechar} style={{
            padding: '8px 18px', borderRadius: '5px',
            border: '1px solid rgba(255,255,255,0.5)', backgroundColor: 'transparent',
            color: COR.branco, fontSize: '13px', cursor: 'pointer', fontFamily: FONTE,
          }}>✕ Fechar</button>
        </div>

        {/* ═══ Corpo ═══ */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', backgroundColor: COR.cinzaBg }}>
          {erro ? (
            <div style={{
              padding: '12px 16px', backgroundColor: COR.vermBg, color: COR.vermelho,
              border: `1px solid ${COR.vermelho}`, borderRadius: '6px',
              fontSize: '13px', fontWeight: '600',
            }}>{erro}</div>
          ) : (carregando || !dados) ? (
            // A ORDEM aqui importa: no primeiro render depois de abrir, o
            // efeito de carga ainda nao rodou, entao carregando e false e
            // dados e null. Antes o codigo caia direto no ramo do conteudo e
            // estourava em ind.data_considerada — era a tela branca.
            <div style={{ padding: '60px', textAlign: 'center', color: COR.muted }}>Carregando...</div>
          ) : (
            <>
              {/* ── Filtro: de quem são as horas consideradas (r238) ── */}
              <FiltroHoras
                filtro={dados.filtro_horas}
                selecionados={colabHoras}
                onTrocar={trocarColaboradores}
              />

              {/* ── Indicadores ── */}
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
                <Indicador label="Data Considerada (D -1)" valor={dataBR(ind.data_considerada)} />
                <Indicador label="Avanço Esperado"  valor={`${ind.avanco_esperado}%`} />
                <Indicador label="Avanço Realizado" valor={`${ind.avanco_realizado}%`} alerta={atrasado} />
                <Indicador label="Horas Totais"     valor={hhmm(ind.horas_totais)} />
                <Indicador label="Horas Executadas" valor={hhmm(ind.horas_executadas)} />
                <Indicador label="Horas Lançadas"   valor={hhmm(ind.horas_lancadas)} />
              </div>

              {/* ── Árvore ── */}
              {(!dados.topicos || dados.topicos.length === 0) ? (
                <div style={{
                  padding: '40px', textAlign: 'center', color: COR.muted, fontSize: '13px',
                  backgroundColor: COR.branco, border: `1px solid ${COR.borda}`, borderRadius: '8px',
                }}>
                  {dados.versao
                    ? 'A quantificação deste projeto não tem itens.'
                    : 'Este projeto ainda não tem quantificação — não há itens para executar.'}
                </div>
              ) : dados.topicos.map(t => {
                const aberto2 = !recolhidos.has(t.topico);
                return (
                  <div key={t.topico || 'sem'} style={{ marginBottom: '14px' }}>
                    {/* ── Linha do tópico (barra azul cheia) ── */}
                    <div style={{
                      backgroundColor: COR.azul, color: COR.branco,
                      borderRadius: '8px', padding: '10px 16px',
                      display: 'flex', alignItems: 'center', gap: '16px',
                    }}>
                      <button onClick={() => alternar(t.topico)}
                        title={aberto2 ? 'Recolher' : 'Expandir'}
                        style={{ border: 'none', background: 'transparent', cursor: 'pointer',
                                 padding: 0, display: 'flex', alignItems: 'center' }}>
                        <IconeMais aberto={aberto2} />
                      </button>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '16px', fontWeight: '700',
                                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {t.topico} - {t.descricao}
                        </div>
                        <div style={{ marginTop: '4px' }}>
                          <Pilula texto={t.etapa} invertido />
                        </div>
                      </div>

                      <ParDatas prev={t.data_inicio_prev} real={t.data_fim_real}
                                desvioDias={t.desvio_dias} invertido />
                      <ParHoras prev={t.horas_previstas} real={t.horas_executadas}
                                desvioHoras={t.desvio_horas} invertido />
                      <div style={{ width: '190px', flexShrink: 0 }}>
                        <Barra percentual={t.percentual} invertido />
                      </div>
                    </div>

                    {/* ── Atividades (filhas), com o rabicho da árvore ── */}
                    {aberto2 && (
                      <div style={{ position: 'relative', paddingLeft: '34px', marginTop: '8px' }}>
                        {/* Tronco vertical; para na altura da última filha */}
                        <div style={{
                          position: 'absolute', left: '15px', top: '-8px', bottom: '38px',
                          width: '2px', backgroundColor: COR.azulClaro,
                        }} />
                        {t.atividades.map(a => (
                          <div key={a.id} style={{ position: 'relative', marginBottom: '8px' }}>
                            {/* Braço horizontal até o card */}
                            <div style={{
                              position: 'absolute', left: '-19px', top: '38px',
                              width: '19px', height: '2px', backgroundColor: COR.azulClaro,
                            }} />
                            {/* r239: o card não tem mais padding vertical — ele
                                foi pro miolo. Com o padding no card, a faixa de
                                ações (que é filha do flex) esticava só até a
                                caixa de conteúdo e sobrava branco em cima e
                                embaixo dela. Agora a faixa vai de borda a borda. */}
                            <div style={{
                              backgroundColor: COR.branco,
                              border: `2px solid ${COR.azulClaro}`, borderRadius: '8px',
                              display: 'flex', alignItems: 'stretch',
                              padding: 0, overflow: 'hidden',
                            }}>
                              <div style={{
                                flex: 1, minWidth: 0,
                                display: 'flex', alignItems: 'center', gap: '16px',
                                // r240: padding à direita — é ele que abre o
                                // respiro entre a barra de avanço e a faixa
                                // do botão, que antes se encostavam.
                                padding: '10px 12px 10px 16px',
                              }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{
                                    fontSize: '15px', fontWeight: '700', color: COR.azulClaro,
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                  }}>
                                    {a.codigo} - {a.descricao}
                                  </div>
                                  <div style={{ marginTop: '4px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                    <Pilula texto={a.responsavel_nome} />
                                    <Pilula texto={a.etapa} />
                                  </div>
                                </div>

                                <ParDatas prev={a.data_inicio_prev} real={a.data_fim_real}
                                          desvioDias={a.desvio_dias} />
                                {/* r240: a atividade não mostra mais horas. Elas
                                    continuam somando no tópico, que é onde a
                                    comparação previsto x realizado é lida. */}
                                <div style={{ width: '190px', flexShrink: 0 }}>
                                  <Barra percentual={a.percentual} />
                                </div>
                              </div>

                              {/* Faixa de ações — mesmo padrão da linha de tarefa
                                  (r237): faixa colorida colada na borda direita,
                                  38px, com botões de ícone branco empilhados. O
                                  arredondamento quem dá é o overflow do card, por
                                  isso a faixa não repete o border-radius.
                                  É o gancho pras funcionalidades que ainda vêm. */}
                              <div style={{
                                width: '28px', flexShrink: 0,   // r240: era 38px
                                backgroundColor: COR.azulClaro,
                                display: 'flex', flexDirection: 'column',
                                alignItems: 'center', justifyContent: 'center', gap: '2px',
                              }}>
                                <button
                                  onClick={(e) => { e.stopPropagation(); onAcaoAtividade && onAcaoAtividade(a, t); }}
                                  title={`Ações da atividade ${a.codigo}`}
                                  style={{
                                    border: 'none', background: 'transparent', color: COR.branco,
                                    cursor: 'pointer', padding: '3px',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  }}
                                >
                                  <IconeReticencias tam={18} cor={COR.branco} />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ExecucaoTecnicaModal;
